import { metricLabelSuffix } from "@/pages/reporting/components/metric-label.ts";
import {
  usePeopleAnalyticsDashboards,
  usePeopleAnalyticsWidgetData,
} from "@/queries/reporting/use-people-analytics.query.ts";
import {
  ApiPeopleAnalyticsEmployeeFilter,
  ApiPeopleAnalyticsWidgetDataInput,
} from "@/types/reporting/people-analytics.types.ts";
import { ApiWidgetPreviewInput, ApiWidgetType } from "@/types/reporting/reporting.types.ts";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Системный дашборд retention
const RETENTION_DASHBOARD_KEY = "retention";

function resolveSystemKey(input: ApiWidgetPreviewInput): string {
  const metricKey = metricLabelSuffix(input.query.metrics[0]);
  const dimension = input.query.dimensions?.[0];
  if (input.widget_type === ApiWidgetType.kpi) return `retention.kpi.${metricKey}`;
  if (dimension) return `retention.by.${dimension}.${metricKey}`;
  return `retention.over_time.${metricKey}`;
}

export function useRetentionWidget(input: ApiWidgetPreviewInput, enabled: boolean) {
  const { t } = useTranslation();
  const dashboards = usePeopleAnalyticsDashboards();

  const systemKey = resolveSystemKey(input);
  const widgetId =
    dashboards.data
      ?.find((dashboard) => dashboard.system_key === RETENTION_DASHBOARD_KEY)
      ?.widgets.find((item) => item.widget.system_key === systemKey)?.widget.id ?? null;

  const dataInput = useMemo<ApiPeopleAnalyticsWidgetDataInput>(() => {
    const filter: ApiPeopleAnalyticsEmployeeFilter = {};
    (input.query.filters ?? []).forEach((item) => {
      filter[item.field] = item.ids;
    });
    return {
      date_from: input.period.date_from as Date,
      date_to: input.period.date_to as Date,
      date_grain: input.query.date_grain ?? null,
      filter,
    };
  }, [input]);
  const analytics = usePeopleAnalyticsWidgetData(widgetId, dataInput, enabled);

  const data = useMemo(
    () =>
      analytics.data && {
        ...analytics.data,
        series: analytics.data.series.map((series) => ({
          ...series,
          points: series.points.map((point) =>
            point.label ? point : { ...point, label: t("reporting:people_analytics_group_empty") }
          ),
        })),
      },
    [analytics.data, t]
  );

  return {
    data,
    isLoading: dashboards.isLoading || analytics.isLoading,
    isError: analytics.isError,
  };
}
